import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SiInlineNotificationComponent } from '@siemens/element-ng/inline-notification';
import { SiLoadingSpinnerComponent } from '@siemens/element-ng/loading-spinner';
import * as L from 'leaflet';

import { BROUTER_MAP_CONFIG } from './config/brouter-map.config';
import { LatLng, MapViewport, TrackStats, Waypoint } from './models/brouter-map.model';
import { LayersConfigService, MapLayerDefinition } from './services/layers-config.service';
import { createWaypoint, RouteEditorService } from './services/route-editor.service';
import { buildInclineChunks } from './services/route-quality';
import { buildDistanceMarkers, cumulativeDistances } from './services/track-geometry';
import { ElevationChartComponent } from './ui/elevation-chart/elevation-chart.component';
import { ExportDialogComponent } from './ui/export-dialog/export-dialog.component';
import { MapSearchComponent, MapSearchResult } from './ui/map-search/map-search.component';
import { MapToolbarComponent } from './ui/map-toolbar/map-toolbar.component';
import { MapViewControlsComponent } from './ui/map-view-controls/map-view-controls.component';
import { RouteDataTableComponent } from './ui/route-data-table/route-data-table.component';
import { StatsFooterComponent } from './ui/stats-footer/stats-footer.component';
import {
  LoadedTrack,
  TrackLoaderDialogComponent
} from './ui/track-loader-dialog/track-loader-dialog.component';

/** How close the pointer must be to the track before the hover readout appears. */
const TRACK_HOVER_TOLERANCE_PX = 12;

@Component({
  selector: 'app-brouter-map',
  imports: [
    TranslateModule,
    SiLoadingSpinnerComponent,
    SiInlineNotificationComponent,
    MapToolbarComponent,
    StatsFooterComponent,
    MapSearchComponent,
    ElevationChartComponent,
    RouteDataTableComponent,
    ExportDialogComponent,
    TrackLoaderDialogComponent,
    MapViewControlsComponent
  ],
  templateUrl: './brouter-map.component.html',
  styleUrl: './brouter-map.component.scss',
  providers: [RouteEditorService],
  host: {
    '(document:keydown)': 'onKeydown($event)'
  }
})
export class BrouterMapComponent implements AfterViewInit, OnDestroy {
  /** Route to display. Changing this replaces the edited route. */
  readonly waypoints = input<Waypoint[]>([]);
  /** Disables all editing, for the saved-track preview. */
  readonly readonlyMap = input(false, { alias: 'readonly' });
  /** Fit the view to the track once it has been routed. */
  readonly autoFit = input(true);
  /** Initial viewport to restore when Leaflet is recreated inside a modal. */
  readonly initialViewport = input<MapViewport | null>(null);

  readonly routeChanged = output<Waypoint[]>();
  readonly statsChanged = output<TrackStats>();
  readonly viewportChanged = output<MapViewport>();
  readonly routingError = output<string>();
  readonly exportRequested = output<void>();
  readonly clearRequested = output<void>();
  readonly editRequested = output<void>();

  readonly routeEditor = inject(RouteEditorService);

  private readonly config = inject(BROUTER_MAP_CONFIG);
  private readonly layersConfig = inject(LayersConfigService);
  private readonly translateService = inject(TranslateService);

  private readonly mapContainer = viewChild.required<ElementRef<HTMLElement>>('mapContainer');

  /** True once Leaflet has attached, so the template can hide its loading state. */
  readonly isReady = signal(false);
  /** While active, each new waypoint creates a straight leg instead of a routed one. */
  readonly beelineMode = signal(false);
  /** Collapsed by default, like the legacy elevation footer. */
  readonly showElevation = signal(false);
  /** Collapsed by default, like the legacy data sidebar tab. */
  readonly showDataTable = signal(false);
  readonly showExportDialog = signal(false);
  readonly showLoaderDialog = signal(false);
  /** Enabled on load, matching the legacy client which started in draw mode. */
  readonly drawMode = signal(true);
  readonly showDistanceMarkers = signal(false);
  readonly showRouteQuality = signal(false);
  readonly isRouting = this.routeEditor.isRouting;
  readonly stats = this.routeEditor.stats;
  readonly error = this.routeEditor.error;

  /** Engine diagnostics are translated here; the raw text is kept on the error itself. */
  readonly errorMessage = computed(() => {
    const error = this.error();
    return error ? this.translateService.instant(error.translationKey) : null;
  });

  readonly hasRoute = computed(() => this.routeEditor.waypoints().length > 0);
  /** True once at least one leg has geometry to plot. */
  readonly hasTrack = computed(() => this.routeEditor.trackLatLngs().length > 1);

  private map?: L.Map;
  private baseLayer?: L.TileLayer;
  private readonly overlayLayers = new Map<string, L.TileLayer>();
  private trackLayer?: L.LayerGroup;
  private readonly markers = new Map<string, L.Marker>();
  private markerLayer?: L.LayerGroup;
  private resizeObserver?: ResizeObserver;
  private highlightMarker?: L.CircleMarker;
  private distanceMarkerLayer?: L.LayerGroup;
  private qualityLayer?: L.LayerGroup;
  /** Dashed line from the last waypoint to the pointer while drawing. */
  private trailerLine?: L.Polyline;
  private trackTooltip?: L.Tooltip;
  /** Track geometry cached at render time to keep pointer moves cheap. */
  private hoverTrack: readonly LatLng[] = [];
  private hoverDistances: readonly number[] = [];
  private hoverElevations: readonly (number | null)[] = [];
  /** Guards against re-emitting a route that arrived through the input. */
  private applyingInput = false;
  /** Set when a route arrives from outside, so only those routes recentre the map. */
  private shouldFitNextTrack = false;

  constructor() {
    effect(() => {
      const incoming = this.waypoints();
      // `setWaypoints` reroutes synchronously, which reads the editor's own signals.
      // Without `untracked` this effect would depend on them and undo every map click.
      untracked(() => {
        this.applyingInput = true;
        this.routeEditor.setWaypoints([...incoming]);
        // Only a route that arrived from outside should move the viewport.
        this.shouldFitNextTrack = incoming.length > 0;
      });
    });

    effect(() => {
      const legs = this.routeEditor.legPaths();
      const track = this.routeEditor.trackLatLngs();
      untracked(() => {
        this.renderTrack(legs);
        this.renderDistanceMarkers(track);
        this.renderRouteQuality(track);
        if (this.autoFit() && this.shouldFitNextTrack && track.length > 0) {
          this.shouldFitNextTrack = false;
          this.fitToTrack(track);
        }
      });
    });

    effect(() => {
      const visible = this.showDistanceMarkers();
      untracked(() => {
        if (!visible) {
          this.distanceMarkerLayer?.clearLayers();
        } else {
          this.renderDistanceMarkers(this.routeEditor.trackLatLngs());
        }
      });
    });

    effect(() => {
      const visible = this.showRouteQuality();
      untracked(() => {
        if (!visible) {
          this.qualityLayer?.clearLayers();
        } else {
          this.renderRouteQuality(this.routeEditor.trackLatLngs());
        }
      });
    });

    effect(() => {
      const waypoints = this.routeEditor.waypoints();
      untracked(() => {
        this.renderMarkers(waypoints);
        if (this.applyingInput) {
          this.applyingInput = false;
          return;
        }
        this.routeChanged.emit(waypoints);
      });
    });

    effect(() => this.statsChanged.emit(this.stats()));

    effect(() => {
      const message = this.errorMessage();
      if (message) {
        this.routingError.emit(message);
      }
    });

    effect(() => {
      const baseLayerId = this.layersConfig.activeBaseLayerId();
      untracked(() => this.applyBaseLayer(baseLayerId));
    });

    effect(() => {
      const overlayIds = this.layersConfig.activeOverlayIds();
      untracked(() => this.applyOverlays(overlayIds));
    });
  }

  ngAfterViewInit(): void {
    this.createMap();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.map?.remove();
    this.map = undefined;
  }

  /** Leaflet mis-renders when its container was hidden at creation, e.g. inside a modal. */
  invalidateSize(): void {
    this.map?.invalidateSize();
  }

  toggleBeelineMode(): void {
    this.beelineMode.update(active => !active);
  }

  toggleDrawMode(): void {
    this.drawMode.update(active => !active);
    if (!this.drawMode()) {
      this.renderTrailer(null);
    }
  }

  zoomIn(): void {
    this.map?.zoomIn();
  }

  zoomOut(): void {
    this.map?.zoomOut();
  }

  /**
   * Draws the pending leg from the last waypoint to the pointer, as the legacy client's
   * draw trailer did, so the next click's effect is visible before committing to it.
   */
  private renderTrailer(cursor: L.LatLng | null): void {
    const waypoints = this.routeEditor.waypoints();
    const last = waypoints[waypoints.length - 1];

    if (!this.map || !cursor || !this.drawMode() || !last) {
      this.trailerLine?.remove();
      this.trailerLine = undefined;
      return;
    }

    const points: L.LatLngExpression[] = [[last.lat, last.lng], cursor];

    if (this.trailerLine) {
      this.trailerLine.setLatLngs(points);
      return;
    }

    this.trailerLine = L.polyline(points, {
      color: this.accentColour(),
      weight: 3,
      opacity: 0.8,
      dashArray: '6 8',
      interactive: false
    }).addTo(this.map);
  }

  /** Read from the theme so map graphics follow light and dark palettes. */
  private themeColour(token: string, fallback: string): string {
    const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    return value || fallback;
  }

  private accentColour(): string {
    return this.themeColour('--element-ui-0', '#006b80');
  }

  /** Brighter sibling of the waypoint colour, so the line reads against the base map. */
  private routeColour(): string {
    return this.themeColour('--element-data-1', '#009999');
  }

  toggleDistanceMarkers(): void {
    this.showDistanceMarkers.update(visible => !visible);
  }

  toggleRouteQuality(): void {
    this.showRouteQuality.update(visible => !visible);
  }

  toggleElevation(): void {
    this.showElevation.update(visible => !visible);
  }

  toggleDataTable(): void {
    this.showDataTable.update(visible => !visible);
    if (!this.showDataTable()) {
      this.highlightPoint(null);
    }
  }

  /** Marks the track position of the row the user is hovering in the data table. */
  highlightPoint(point: LatLng | null): void {
    if (!this.map) {
      return;
    }

    if (!point) {
      this.highlightMarker?.remove();
      this.highlightMarker = undefined;
      return;
    }

    const latLng = L.latLng(point.lat, point.lng);
    if (this.highlightMarker) {
      this.highlightMarker.setLatLng(latLng);
    } else {
      this.highlightMarker = L.circleMarker(latLng, {
        radius: 7,
        color: 'yellow',
        weight: 3,
        fillOpacity: 0.4,
        interactive: false
      }).addTo(this.map);
    }
  }

  onSearchResult(result: MapSearchResult): void {
    // Prefer the bounding box so a city fills the view rather than centring on a point.
    if (result.bbox?.isValid()) {
      this.map?.fitBounds(result.bbox, { padding: [32, 32] });
    } else {
      this.map?.setView(result.center, 14);
    }
  }

  /** Replaces the current route with an imported track and zooms to it. */
  onTrackLoaded(track: LoadedTrack): void {
    this.routeEditor.setWaypoints(track.points.map(point => createWaypoint(point)));
    this.showLoaderDialog.set(false);
    this.shouldFitNextTrack = true;
  }

  /**
   * Mirrors the legacy shortcuts: B beeline, R reverse, Z delete last point,
   * Backspace clear. Ignored while the user is typing.
   */
  onKeydown(event: KeyboardEvent): void {
    if (this.readonlyMap() || !this.isReady() || this.isTypingTarget(event.target)) {
      return;
    }

    switch (event.key.toLowerCase()) {
      case 'b':
        this.toggleBeelineMode();
        break;
      case 'd':
        this.toggleDrawMode();
        break;
      case 'c':
        this.toggleRouteQuality();
        break;
      case 'r':
        this.routeEditor.reverse();
        break;
      case 'z':
        this.routeEditor.deleteLastPoint();
        break;
      case 'backspace':
        event.preventDefault();
        this.clearRequested.emit();
        break;
      default:
        return;
    }
  }

  private isTypingTarget(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element) {
      return false;
    }
    return element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName);
  }

  fitToTrack(track: readonly LatLng[] = this.routeEditor.trackLatLngs()): void {
    if (!this.map || track.length === 0) {
      return;
    }
    this.map.fitBounds(L.latLngBounds(track.map(p => L.latLng(p.lat, p.lng))), {
      padding: [32, 32]
    });
  }

  private createMap(): void {
    const container = this.mapContainer().nativeElement;
    const initialViewport = this.initialViewport();

    this.map = L.map(container, {
      center: initialViewport
        ? [initialViewport.center.lat, initialViewport.center.lng]
        : [this.config.initialMapLocation.lat, this.config.initialMapLocation.lng],
      zoom: initialViewport?.zoom ?? this.config.initialMapZoom,
      // Zoom is offered through the Element action bar instead.
      zoomControl: false,
      attributionControl: true
    });

    this.markerLayer = L.layerGroup().addTo(this.map);
    this.trackLayer = L.layerGroup().addTo(this.map);
    this.qualityLayer = L.layerGroup().addTo(this.map);
    this.distanceMarkerLayer = L.layerGroup().addTo(this.map);

    this.applyBaseLayer(this.layersConfig.activeBaseLayerId());
    this.applyOverlays(this.layersConfig.activeOverlayIds());

    if (!this.readonlyMap()) {
      this.map.on('click', event => {
        if (this.drawMode()) {
          this.routeEditor.addWaypoint(event.latlng, undefined, this.beelineMode());
        }
      });
      this.map.on('mousemove', event => this.renderTrailer(event.latlng));
      this.map.on('mouseout', () => this.renderTrailer(null));
    }

    this.map.on('mousemove', event => this.updateTrackReadout(event.latlng));
    this.map.on('mouseout', () => this.closeTrackReadout());
    this.map.on('moveend zoomend', () => this.emitViewport());

    // A modal or fullscreen toggle resizes the container without a window resize event.
    this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize());
    this.resizeObserver.observe(container);

    this.isReady.set(true);
    this.renderTrack(this.routeEditor.legPaths());
    this.renderMarkers(this.routeEditor.waypoints());
  }

  private emitViewport(): void {
    if (!this.map) {
      return;
    }
    const center = this.map.getCenter();
    this.viewportChanged.emit({
      center: { lat: center.lat, lng: center.lng },
      zoom: this.map.getZoom()
    });
  }

  private applyBaseLayer(id: string): void {
    if (!this.map) {
      return;
    }
    const definition = this.layersConfig.getBaseLayer(id);
    if (!definition) {
      return;
    }
    this.baseLayer?.remove();
    this.baseLayer = this.createTileLayer(definition).addTo(this.map);
    this.baseLayer.bringToBack();
  }

  private applyOverlays(ids: readonly string[]): void {
    if (!this.map) {
      return;
    }
    for (const [id, layer] of this.overlayLayers) {
      if (!ids.includes(id)) {
        layer.remove();
        this.overlayLayers.delete(id);
      }
    }
    for (const id of ids) {
      if (this.overlayLayers.has(id)) {
        continue;
      }
      const definition = this.layersConfig.getOverlay(id);
      if (definition) {
        this.overlayLayers.set(id, this.createTileLayer(definition).addTo(this.map));
      }
    }
  }

  private createTileLayer(definition: MapLayerDefinition): L.TileLayer {
    return L.tileLayer(definition.urlTemplate, {
      attribution: definition.attribution,
      maxZoom: definition.maxZoom,
      ...(definition.subdomains ? { subdomains: definition.subdomains } : {})
    });
  }

  /**
   * Draws one polyline per leg.
   *
   * Legs are drawn separately, rather than as a single track, so a click can be
   * attributed to a leg and split it with a new waypoint.
   */
  private renderTrack(legs: readonly LatLng[][]): void {
    if (!this.map || !this.trackLayer) {
      return;
    }

    this.trackLayer.clearLayers();

    const opacity = this.config.defaultOpacity;
    const beelineFlags = this.routeEditor.beelineFlags();
    // Cached for the hover readout so pointer moves do not recompute the whole track.
    this.hoverTrack = this.routeEditor.trackLatLngs();
    this.hoverDistances = cumulativeDistances(this.hoverTrack);
    this.hoverElevations = this.routeEditor.trackElevations();

    legs.forEach((leg, index) => {
      if (leg.length === 0) {
        return;
      }
      const latLngs = leg.map(p => L.latLng(p.lat, p.lng));
      const dashArray = beelineFlags[index] ? '1 10' : undefined;

      // The white casing underneath keeps the route legible on dark imagery.
      L.polyline(latLngs, {
        color: 'white',
        weight: 8,
        opacity,
        interactive: false,
        ...(dashArray ? { dashArray } : {})
      }).addTo(this.trackLayer!);

      const line = L.polyline(latLngs, {
        color: this.routeColour(),
        weight: 5,
        opacity,
        interactive: !this.readonlyMap(),
        ...(dashArray ? { dashArray } : {})
      }).addTo(this.trackLayer!);

      if (!this.readonlyMap()) {
        line.on('click', event => {
          // Otherwise the map click handler would also append a waypoint at the end.
          L.DomEvent.stopPropagation(event);
          this.routeEditor.insertWaypointAt(index + 1, event.latlng);
        });
      }
    });
  }

  /**
   * Readout of the track point nearest the pointer.
   *
   * Driven from the map rather than a polyline tooltip: the route is covered by the quality
   * and distance-marker layers, so hover events do not reliably reach the line itself.
   */
  private updateTrackReadout(cursor: L.LatLng): void {
    const track = this.hoverTrack;
    if (!this.map || track.length < 2) {
      this.closeTrackReadout();
      return;
    }

    const nearest = this.nearestTrackIndex(cursor);
    const point = track[nearest];
    const cursorPixel = this.map.latLngToContainerPoint(cursor);
    const pointPixel = this.map.latLngToContainerPoint([point.lat, point.lng]);

    if (cursorPixel.distanceTo(pointPixel) > TRACK_HOVER_TOLERANCE_PX) {
      this.closeTrackReadout();
      return;
    }

    this.trackTooltip ??= L.tooltip({ direction: 'top', offset: [0, -8] });
    this.trackTooltip
      .setLatLng([point.lat, point.lng])
      .setContent(this.trackPointSummary(nearest))
      .addTo(this.map);
  }

  private closeTrackReadout(): void {
    this.trackTooltip?.remove();
  }

  /** Cheap planar comparison; exact distance is not needed to pick the closest point. */
  private nearestTrackIndex(cursor: L.LatLng): number {
    const scale = Math.cos((cursor.lat * Math.PI) / 180);
    let nearest = 0;
    let shortest = Number.POSITIVE_INFINITY;

    this.hoverTrack.forEach((point, index) => {
      const dLat = point.lat - cursor.lat;
      const dLng = (point.lng - cursor.lng) * scale;
      const gap = dLat * dLat + dLng * dLng;
      if (gap < shortest) {
        shortest = gap;
        nearest = index;
      }
    });

    return nearest;
  }

  /** Distance along the route and altitude at the given track index. */
  private trackPointSummary(index: number): string {
    const metres = this.hoverDistances[index] ?? 0;
    const distance =
      metres >= 1000
        ? `${(metres / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} km`
        : `${metres.toLocaleString(undefined, { maximumFractionDigits: 0 })} m`;

    const rows = [
      `${this.translateService.instant('BROUTER_MAP.ELEVATION.DISTANCE')}: ${distance}`
    ];

    const elevation = this.hoverElevations[index];
    if (elevation !== null && elevation !== undefined) {
      const altitude = Math.round(elevation).toLocaleString();
      rows.push(
        `${this.translateService.instant('BROUTER_MAP.ELEVATION.ALTITUDE')}: ${altitude} m`
      );
    }

    return rows.join('<br>');
  }

  private renderMarkers(waypoints: readonly Waypoint[]): void {
    if (!this.map || !this.markerLayer) {
      return;
    }

    const seen = new Set<string>();

    waypoints.forEach((waypoint, index) => {
      seen.add(waypoint.id);
      // Reassigned on every render: inserting or reversing shifts every later number.
      const icon = this.createIcon(index + 1);

      const existing = this.markers.get(waypoint.id);
      if (existing) {
        existing.setLatLng([waypoint.lat, waypoint.lng]);
        existing.setIcon(icon);
        return;
      }

      const marker = L.marker([waypoint.lat, waypoint.lng], {
        draggable: !this.readonlyMap(),
        icon,
        title: waypoint.name ?? ''
      });

      if (!this.readonlyMap()) {
        marker.on('dragend', () => this.routeEditor.moveWaypoint(waypoint.id, marker.getLatLng()));
        marker.on('click', () => this.routeEditor.removeWaypoint(waypoint.id));
      }

      marker.addTo(this.markerLayer!);
      this.markers.set(waypoint.id, marker);
    });

    for (const [id, marker] of this.markers) {
      if (!seen.has(id)) {
        marker.remove();
        this.markers.delete(id);
      }
    }
  }

  /** Kilometre markers along the track, replacing `leaflet-distance-marker`. */
  private renderDistanceMarkers(track: readonly LatLng[]): void {
    if (!this.distanceMarkerLayer) {
      return;
    }

    this.distanceMarkerLayer.clearLayers();
    if (!this.showDistanceMarkers() || track.length < 2) {
      return;
    }

    for (const marker of buildDistanceMarkers(track)) {
      L.marker([marker.position.lat, marker.position.lng], {
        interactive: false,
        icon: L.divIcon({
          className:
            'brouter-map__distance-marker d-flex align-items-center justify-content-center',
          html: `<span class="px-2 rounded-1 fw-bold text-nowrap">${marker.km}</span>`,
          iconSize: [26, 16],
          iconAnchor: [13, 8]
        })
      }).addTo(this.distanceMarkerLayer);
    }
  }

  /** Gradient colouring, replacing the `leaflet-hotline` route quality overlay. */
  private renderRouteQuality(track: readonly LatLng[]): void {
    if (!this.qualityLayer) {
      return;
    }

    this.qualityLayer.clearLayers();
    if (!this.showRouteQuality() || track.length < 2) {
      return;
    }

    const chunks = buildInclineChunks(track, this.routeEditor.trackElevations());
    for (const chunk of chunks) {
      L.polyline(
        chunk.points.map(point => L.latLng(point.lat, point.lng)),
        { color: chunk.colour, weight: 6, opacity: 0.95, interactive: false }
      ).addTo(this.qualityLayer);
    }
  }

  private createIcon(position: number): L.DivIcon {
    return L.divIcon({
      className: 'd-flex align-items-center justify-content-center',
      html:
        '<span class="brouter-map__marker-pin d-flex align-items-center justify-content-center ' +
        `rounded-circle fw-bold">${position}</span>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
  }
}
